import React, { useState, useEffect } from 'react';
import {
  Box,
  Chip,
  Typography,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { palette, spaces } from '../../../assets/styles/themes/tokens';
import useScreenSize from '../../../utils/useScreenSize';
import { useTranslation } from 'react-i18next';
import { DiscountInformation, LicenceModel } from '@klab-berlin/api-sdk/lib/types/responses/Billing';
import { currencyFormat } from '../../../utils/format';
import { PaymentPeriod } from '@klab-berlin/api-sdk/lib/types/common';
import { useSelector } from 'react-redux';
import { getSubscriptionUpgradePreview, selectUser } from '../../../reducers/user.selectors';
import { getSubscription } from '../../../reducers/user.selectors';
import SubscriptionPaymentDetails from '../../../components/SubscriptionPaymentDetails';
import services from '../../../services';
import useRouter from '../../../utils/useRouter';

interface LicenceInfoProps {
  licence: LicenceModel;
  library?: string;
  toggleSelectedLicenceModel: () => void;
  promoCode: string;
  promoCodeDiscountInfo?: DiscountInformation;
  setPromoCode: (promoCode: string) => void;
  setPromoCodeDiscountInfo: (discountInfo?: DiscountInformation) => void;
  setSelectedPaymentRhythm: (paymentPeriod: PaymentPeriod) => void;
  selectedPaymentRhythm: PaymentPeriod;
  paymentRhythms: PaymentPeriod[];
}

const LicenceInfo: React.FC<LicenceInfoProps> = (props) => {
  const { t } = useTranslation();
  const {
    licence,
    library,
    toggleSelectedLicenceModel,
    promoCode,
    setSelectedPaymentRhythm,
    selectedPaymentRhythm,
    paymentRhythms,
    setPromoCodeDiscountInfo: setDiscountInfo,
    setPromoCode,
  } = props;

  const {
    name,
    landingPageTitle,
    displayTextLines,
    isTrial,
    isRef,
    price,
    period,
    totalPrice
  } = licence;

  const { isLgScreen, isMdScreen } = useScreenSize();
  const isDesktopScreen = isLgScreen || isMdScreen;
  const licenceHasTraineePromoCode = licence.refDiscount?.type === 'promoCode';
  const licenceTraineePromoCode = licenceHasTraineePromoCode && licence.refDiscount?.value
    ? licence.refDiscount.value
    : '';
  const isRefViaPromoCode = licenceHasTraineePromoCode && promoCode === licenceTraineePromoCode;
  const [isTraineeDiscountEnabled, setIsTraineeDiscountEnabled] =
    useState<boolean>(isRefViaPromoCode || isRef || false);
  const [newTotalPrice, setNewTotalPrice] = useState<number | undefined>(undefined);
  const [promoCodeDiscountInfo, setPromoCodeDiscountInfo] = useState<DiscountInformation>();
  const subscriptionUpgradePreview = useSelector(getSubscriptionUpgradePreview);
  const currentSubscription = useSelector(getSubscription);
  const user = useSelector(selectUser);
  const router = useRouter();
  const promoCodeInQuery = !!router?.match?.location?.query?.promoCode;

  // Fetch discount info if current subscription has a promo code
  useEffect(() => {
    const fetchSubscriptionPromoCodeDiscount = async () => {
      // Fetch discount info if subscription has a promo code and it's different from what we have
      const hasSubscriptionPromoCode = currentSubscription?.promoCode;
      const isSubscriptionPromoCodeDifferent = hasSubscriptionPromoCode && currentSubscription.promoCode !== promoCode;
      const shouldFetch = isSubscriptionPromoCodeDifferent && !promoCodeDiscountInfo;

      if (shouldFetch) {
        const discountInfo = await services.billing.getDiscountInformation(
          currentSubscription.promoCode,
          licence.licenceModelId
        );
        if (discountInfo.valid) {
          setPromoCodeDiscountInfo(discountInfo);
          setDiscountInfo(discountInfo);
          setNewTotalPrice(discountInfo.newTotalPrice);
          setPromoCode(currentSubscription.promoCode);
        }
      }
    };

    fetchSubscriptionPromoCodeDiscount();
  }, [
    currentSubscription?.promoCode,
    licence.licenceModelId,
    promoCode,
    promoCodeDiscountInfo,
    setDiscountInfo,
    setPromoCode
  ]);

  const hasSubscriptionPreviewData = !!((user?.isBibliothekUser || user?.isCourseUser) && subscriptionUpgradePreview);
  const hidePromoCode = (user?.isBibliothekUser || user?.isCourseUser)
    || (isRefViaPromoCode && !promoCodeInQuery) || (isTrial && !promoCode);

  const hasNewPrice = newTotalPrice !== undefined;
  const previousPrice = hasNewPrice ? totalPrice : undefined;
  const currentPrice = hasNewPrice ? newTotalPrice : totalPrice;

  const handlePromoCodeValidated = (discountInformation: DiscountInformation | undefined, promoCode: string) => {
    setPromoCodeDiscountInfo(discountInformation);
    setDiscountInfo(discountInformation);
    setNewTotalPrice(discountInformation?.newTotalPrice);
    setPromoCode(promoCode);
    if (!isRef && !promoCode && !!licenceTraineePromoCode) {
      setIsTraineeDiscountEnabled(false);
    }
  };

  const handlePromoCodeActiveLayoutInit = () => {
    setPromoCodeDiscountInfo(undefined);
    setDiscountInfo(undefined);
    setNewTotalPrice(undefined);
  };

  const getPricePerMonth = (price: number) => promoCodeDiscountInfo?.newPricePerMonth
    ? currencyFormat(promoCodeDiscountInfo?.newPricePerMonth)
    : currencyFormat(price);

  const handleTraineePromocodeToggle = async () => {
    if (isTraineeDiscountEnabled && promoCode === licenceTraineePromoCode) {
      handlePromoCodeValidated(undefined, ''); // Clear promoCode for old pricing
      setIsTraineeDiscountEnabled(false);
    } else {
      const discountInformation = await services.
        billing.getDiscountInformation(licenceTraineePromoCode, licence.licenceModelId);
      handlePromoCodeValidated(discountInformation, licenceTraineePromoCode);
      setIsTraineeDiscountEnabled(true);
    }
  };

  const handleTraineeDiscountToggle = async () => {
    if (licenceTraineePromoCode) {
      await handleTraineePromocodeToggle();
    } else {
      toggleSelectedLicenceModel();
      setIsTraineeDiscountEnabled(prevState => !prevState);
    }
  };

  const getAmountDueTodayFromLicence = () => {
    const priceForPaymentRythms: { [key in PaymentPeriod]: string } = {
      monthly: currencyFormat(price),
      quarterly: currencyFormat(price * 3),
      annually: currencyFormat(price * 12),
      fullAmount: currencyFormat(totalPrice)
    };

    return priceForPaymentRythms[selectedPaymentRhythm];
  };

  const getAmountDueTodayFromDiscountInfo = (promoCodeDiscountInfo: DiscountInformation) => {
    return currencyFormat(promoCodeDiscountInfo.firstPayment[selectedPaymentRhythm]);
  };

  let amountDueToday = currencyFormat(0);
  if (!isTrial) {
    amountDueToday = promoCodeDiscountInfo && promoCodeDiscountInfo.valid
      ? getAmountDueTodayFromDiscountInfo(promoCodeDiscountInfo)
      : getAmountDueTodayFromLicence();
  }

  const renderLicenceDescription = () => {
    let descriptionText = displayTextLines && displayTextLines.join(' ');

    if (!library && subscriptionUpgradePreview?.allowToUpgrade) {
      if (user?.isBibliothekUser) {
        descriptionText = subscriptionUpgradePreview.payPeriod === 12 ? t(
          'When switching to meinUnterricht 360, the price of your subscription will increase by <totalAmount>. ' +
          'The existing contract duration until <oldSubscriptionEndDate> remains unchanged.',
          {
            totalAmount: currencyFormat(subscriptionUpgradePreview.totalAmount),
            oldSubscriptionEndDate: subscriptionUpgradePreview.oldSubscription.endDate,
          }
        ) : t(
          'When switching to meinUnterricht 360, the price of your subscription will increase to ' +
          '<totalAmount> until the end of your contract term. ' +
          'The existing contract duration until <oldSubscriptionEndDate> remains unchanged.',
          {
            totalAmount: currencyFormat(subscriptionUpgradePreview.totalAmount),
            oldSubscriptionEndDate: subscriptionUpgradePreview.oldSubscription.endDate,
          }
        );
      } else if (user?.isCourseUser) {
        descriptionText = t(
          'You will be upgraded from your current Schulflix meineFortbildung subscription ' +
          '(<oldPricePerMonth>) to meinUnterricht 360 (<newPricePerMonth>). Your subscription period ' +
          'will remain unchanged until <oldSubscriptionEndDate>.',
          {
            oldPricePerMonth: currencyFormat(Number(subscriptionUpgradePreview.oldLicence.pricePerMonth)),
            newPricePerMonth: currencyFormat(Number(subscriptionUpgradePreview.newLicence.pricePerMonth)),
            oldSubscriptionEndDate: subscriptionUpgradePreview.oldSubscription.endDate,
          }
        );
      }
    }

    const showRefToggle = () => {
      if (!licence.newPricing) {
        if (user?.isCourseUser) {
          return false;
        }

        return !!licenceTraineePromoCode || (isTrial && !promoCode);
      } else {
        if (user) {
          return false;
        }

        if (isTrial) {
          return true;
        }

        return false;
      }
    };

    return (
      <>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            flexDirection: isDesktopScreen ? 'row' : 'column',
            gap: spaces.xs,
          }}
        >
          <Typography
            variant='h4'
            color={palette.text.primary}
          >
            {landingPageTitle || name}
          </Typography>
          {(isTrial || isRefViaPromoCode || isRef) && (
            <Chip
              data-testid='premium-chip'
              label={t(isRefViaPromoCode || isRef ? 'For trainees' : 'meinUnterrich Pro')}
              variant='outlined'
              color='primary'
              size='small'
            />
          )}
        </Box>
        {descriptionText && (
          <Typography variant='body2' data-testid='licence-description-text'>
            {descriptionText}
          </Typography>
        )}
        {showRefToggle() && (
          <FormControlLabel
            control={
              <Switch
                data-testid='trainee-discount-toggle'
                checked={isTraineeDiscountEnabled}
                onChange={handleTraineeDiscountToggle}
                inputProps={{ 'aria-label': 'controlled' }}
              />
            }
            label={t('Trainee Discount')}
            labelPlacement='start'
            sx={{
              width: 'fit-content',
              '&.MuiFormControlLabel-root': {
                gap: spaces.xs,
                justifyContent: 'flex-end',
                marginLeft: 0,
                marginBottom: 0,
              },
            }}
          />
        )}
      </>
    );
  };

  const renderLicencePrice = () => {
    return (
      <>
        <Typography
          variant='body2'
          color={palette.text.primary}
        >
          {isTrial ? t('After your test') : t('Your chosen package')}
        </Typography>
        <Box>
          <Typography
            variant='h2'
            color={palette.text.primary}
            data-testid='licence-info-area__monthly-price'
          >
            {getPricePerMonth(price)} mtl.
          </Typography>
          {!library && (
            <Typography
              variant='caption'
              color={palette.text.primary}
            >
              {t('<termLength> months duration', { termLength: period })}
            </Typography>
          )}
        </Box>
      </>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: spaces.lg,
      }}
    >
      <Box
        data-testid='licence-description'
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: spaces.sm,
        }}
      >
        {renderLicenceDescription()}
      </Box>
      {!user?.isBibliothekUser && !user?.isCourseUser && (
        <Box
          data-testid='licence-price'
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: spaces.sm,
          }}
        >
          {renderLicencePrice()}
        </Box>
      )}
      {!library && (
        <Box data-testid='surface-payment-table'>
          <SubscriptionPaymentDetails
            licence={licence}
            hasSubscriptionPreviewData={hasSubscriptionPreviewData}
            previousPrice={previousPrice}
            currentPrice={currentPrice}
            amountDueToday={amountDueToday}
            promoCode={promoCode}
            hidePromoCode={hidePromoCode}
            subscriptionUpgradePreview={subscriptionUpgradePreview}
            currentSubscription={currentSubscription}
            selectedPaymentRhythm={selectedPaymentRhythm}
            paymentRhythms={paymentRhythms}
            setSelectedPaymentRhythm={setSelectedPaymentRhythm}
            handlePromoCodeValidated={handlePromoCodeValidated}
            handlePromoCodeActiveLayoutInit={handlePromoCodeActiveLayoutInit}
            user={user}
            promoCodeDiscountInfo={promoCodeDiscountInfo}
          />
        </Box>
      )}
    </Box>
  );
};

export default LicenceInfo;
